<template>
  <div
    data-el="form-tabs"
    role="tablist"
    :data-tabs-default-index="defaultTab"
    :data-id="dataId"
    :data-tab-count="tabsLength"
  >
    <section class="relative">
      <div
        class="absolute bottom-0 left-0 z-0 w-full border-b border-gray-200"
      />
      <div
        :class="{
          'overflow-x-auto overflow-y-hidden rounded-t-lg bg-slate-50/70 px-1.5 pt-1.5': true,
        }"
      >
        <nav class="flex space-x-2" data-el="tabs-wrapper">
          <div
            class="flex items-center justify-center px-2"
            v-if="$slots.button"
          >
            <slot name="button"></slot>
          </div>
          <button
            v-for="(tab, i) in tabs"
            :key="tab.href"
            type="button"
            :data-tab-index="i.toString(10)"
            role="tab"
            :data-active="tab.isActive() ? '1' : '0'"
            data-el="tab-button"
            :data-test="tab.testId"
            :class="{
              'z-10 -mb-px inline-flex items-center gap-2 whitespace-nowrap rounded-t-lg border border-gray-200 px-5 py-3 text-center text-sm font-medium': true,
              'border-transparent bg-transparent text-gray-500 hover:text-gray-900':
                !tab.isActive(),
              'active border-b-transparent bg-white text-slate-900 shadow-md hover:text-gray-900':
                tab.isActive(),
            }"
            :aria-current="tab.isActive() ? 'page' : undefined"
            @click.prevent="selectTab(tab)"
          >
            <Icon
              class="text-sm text-gray-600"
              v-if="tab.icon"
              :icon="tab.icon"
            />
            {{ tab.title }}
          </button>
        </nav>
      </div>
    </section>
    <div>
      <slot />
    </div>
  </div>
</template>
<script lang="ts" setup>
import { computed, provide, reactive, watch } from 'vue';
import type { TabItemRef } from '~/lib/services/types';
import { PROVIDE_KEY_ADD_TAB } from '~/lib/services/helpers';
import Icon from '~/components/Icon.vue';
import { useNuxtApp } from '#imports';
const { $captureEvent: captureEvent } = useNuxtApp();

const props = withDefaults(
  defineProps<{
    defaultTab?: number;
    dataId?: string;
  }>(),
  {
    defaultTab: 0,
    dataId: '',
  },
);
const model = defineModel<number | undefined>();
const tabs = reactive<TabItemRef[]>([]);
const tabsLength = computed(() => tabs.length);
const emit = defineEmits<{
  (e: 'input', n: number): void;
  (e: 'tab', n: number): void;
}>();
provide(PROVIDE_KEY_ADD_TAB, (tab: TabItemRef) => {
  tabs.push(tab);
});
const selectTab = (selectedTab: TabItemRef) => {
  tabs.forEach((tab: TabItemRef, i: number) => {
    if (tab.title === selectedTab.title) {
      model.value = i;
      emit('input', i);
      emit('tab', i);
      captureEvent('tab_select', { testId: tab.testId, title: tab.title });
      tab.setActive(true);
    } else {
      tab.setActive(false);
    }
  });
};
watch(tabsLength, () => {
  tabs[props.defaultTab].setActive(true);
  for (let i = 0; i < tabsLength.value; i++) {
    if (i !== props.defaultTab) {
      tabs[i].setActive(false);
    }
  }
});
watch(
  () => props.defaultTab,
  () => {
    model.value = props.defaultTab;
    tabs[props.defaultTab].setActive(true);
    for (let i = 0; i < tabsLength.value; i++) {
      if (i !== props.defaultTab) {
        tabs[i].setActive(false);
      }
    }
  },
);
</script>
